/**
 * Transforms one field's inline-editor value into the `{ value, unit?, year? }`
 * shape the templates/answer PATCH endpoint expects. Shared by General Info
 * and ESG Info — both patch the same endpoint shape, one field at a time.
 */
export function serializeFieldAnswer(
  fieldTypeId: string | undefined,
  value: unknown,
): { value: unknown; unit?: string; year?: number | null } {
  // Video/image cleared — backend requires {} not null to clear object fields.
  if (
    value === null &&
    (fieldTypeId === 'VIDEO_UPLOAD' ||
      fieldTypeId === 'VIDEO' ||
      fieldTypeId === 'MULTIPLE_PHOTO_UPLOAD')
  ) {
    return { value: {} };
  }

  if (
    fieldTypeId === 'NUMBER_DYNAMIC_UOM_WITH_YEAR' ||
    fieldTypeId === 'NUMBER_DYNAMIC_CURRENCY_WITH_YEAR'
  ) {
    const compound = value as {
      value: number | null;
      unit: string | null;
      year: number | null;
    } | null;
    if (!compound?.unit) return { value: null };
    return {
      value: compound.value ?? null,
      unit: compound.unit,
      year: compound.year ?? null,
    };
  }

  if (
    fieldTypeId === 'NUMBER_UOM' ||
    fieldTypeId === 'NUMBER_DYNAMIC_UOM' ||
    fieldTypeId === 'NUMBER_DYNAMIC_CURRENCY'
  ) {
    const compound = value as {
      value: number | null;
      unit: string | null;
    } | null;
    if (!compound?.unit) return { value: null };
    return { value: compound.value ?? null, unit: compound.unit };
  }

  if (fieldTypeId === 'TABLE' && Array.isArray(value)) {
    const rows = (value as Record<string, unknown>[]).map(row => {
      const clean: Record<string, unknown> = {};
      for (const [rk, rv] of Object.entries(row)) {
        // Strip null unit/year at row root — backend schema requires string/number
        if ((rk === 'unit' || rk === 'year') && rv === null) continue;
        // Strip nested compound objects where unit is null
        if (rv !== null && typeof rv === 'object' && !Array.isArray(rv)) {
          const nested = rv as Record<string, unknown>;
          if ('unit' in nested && nested.unit === null) {
            const { unit: _u, ...rest } = nested;
            clean[rk] = rest;
            continue;
          }
        }
        clean[rk] = rv;
      }
      return clean;
    });
    return { value: rows };
  }

  // Defensive: if fieldTypeId lookup missed but value looks like a compound
  // { value, unit } object (e.g. unit: null), strip the null unit so the
  // backend doesn't receive `unit: null` which fails schema validation.
  if (value !== null && typeof value === 'object' && !Array.isArray(value)) {
    const maybeCompound = value as Record<string, unknown>;
    if ('unit' in maybeCompound) {
      if (!maybeCompound.unit) return { value: null };
      return {
        value: maybeCompound.value ?? null,
        unit: maybeCompound.unit as string,
        ...('year' in maybeCompound && maybeCompound.year != null
          ? { year: maybeCompound.year as number }
          : {}),
      };
    }
  }

  const serialised =
    fieldTypeId === 'DATE' && typeof value === 'string' && value
      ? new Date(value).toISOString()
      : value;
  return { value: serialised };
}
